import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { Link, useHistory } from "react-router-dom";
import CenteredLoading from "../../components/CenteredLoading";
import { useAuthentication } from "../../context/authentication";
import { consumeReturnUrl } from "../../context/authentication/AuthenticationProvider";
import { Provider } from "../../utils/types";

export enum AuthorizationCodeGrant {
  AuthorizationRequest = "411-authorization-request",
  AuthorizationResponse = "412-authorization-response",
  AccessTokenRequest = "413-access-token-request"
}

interface OAuth2LoaderProps {
  step: AuthorizationCodeGrant;
  provider: Provider;
}

const ERROR_USER = ["access_denied"];

const ERROR_THEIRS = ["server_error", "temporarily_unavailable"];
const ERROR_OURS = [
  "invalid_request",
  "unauthorized_client",
  "unsupported_response_type",
  "invalid_scope"
];

const OAuth2Loader: React.FC<OAuth2LoaderProps> = ({ step, provider }) => {
  const { startLoginProcess, checkIsUserLoggedIn } = useAuthentication();
  const [status, setStatus] = useState<"processing" | "error">("processing");
  const [feedbackText, setFeedbackText] = useState<string>("");
  const location = useLocation();
  const history = useHistory();
  const queryString = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  useEffect(() => {
    if (!queryString.has("error")) return;

    setStatus("error");
    const errorCode = queryString.get("error") || "server_error";

    if (ERROR_OURS.includes(errorCode)) {
      setFeedbackText("There was an error while processing the request.");
    }

    if (ERROR_THEIRS.includes(errorCode)) {
      setFeedbackText(
        `There was an error with ${provider}. Please, try again later.`
      );
    }

    if (ERROR_USER.includes(errorCode)) {
      setFeedbackText(
        `${provider} denied access to you account. Please, try again.`
      );
    }
  }, [provider, queryString]);

  useEffect(() => {
    if (queryString.has("error")) return;

    switch (step) {
      case AuthorizationCodeGrant.AuthorizationRequest: {
        setFeedbackText(`Redirecting you to ${provider}...`);
        startLoginProcess(
          provider,
          queryString.get("returnToUrl") ?? undefined
        );
        return;
      }
      case AuthorizationCodeGrant.AuthorizationResponse: {
        (async () => {
          await checkIsUserLoggedIn();
          history.replace(consumeReturnUrl() ?? "/");
        })();

        return;
      }
    }
  }, [
    provider,
    startLoginProcess,
    queryString,
    step,
    checkIsUserLoggedIn,
    history
  ]);

  return (
    <div className="flex flex-column items-center justify-center min-h-screen dark:bg-gray-900 dark:text-white">
      <div className="text-center">
        {status === "processing" && (
          <CenteredLoading className="object-center" />
        )}

        <h5>{feedbackText}</h5>

        {status === "error" && (
          <Link to={consumeReturnUrl() ?? "/"}>Back to Hotspotty</Link>
        )}
      </div>
    </div>
  );
};

export default OAuth2Loader;
